import React from "react";
import Select from "react-select";
import { ButtonGroup, Button } from "aible";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faArrowCircleUp,
  faArrowCircleDown,
  faEye,
  faCog,
  faThumbsUp,
  faThumbsDown,
  faCheckCircle,
  faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";

import SelectedFeatureChart from "components/FeatureChart/SelectedFeatureChart";
import FeatureChartContainer from "containers/FeatureChart/FeatureChart";
import PartialDepPlotContainer from "containers/RunPlot/PartialDepPlot";
import WizardModelContainer from "containers/Wizard/WizardModel";

import LeakageChartContainer from "containers/Next/Charts/LeakageChart";
import NextChartContainer from "components/Next/Charts/NextChart";
import NextSearchContainer from "containers/Next/NextSearch";

import ChooseProcessLimitFrontierContainer from "containers/CreateWizard/Constraints/ChooseProcessLimitFrontier";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { LineInput } from "components/Inputs/LineInput";

import CapSlider from "components/Project/CapSlider";

import BullseyeBlue from "images/bullseye-arrow-light-blue.svg";
import BullseyeLight from "images/bullseye-arrow-light.svg";
import AcademicBlue from "images/academic-blu.svg";
import ThumbsUp from "images/tup.svg";
import ThumbsDown from "images/tdown.svg";
import SettingsIcon from "images/settings.svg";

import { STEP_TYPE, WIZARD_SECTIONS, ORIGINAL_STEPS } from "containers/Wizard/reducer";
import { PROJECT_MODES } from "containers/Run/reducer";

import { isEnabled } from "utils/featureFlags";

import styles from "./style.module.css";

export default class WizardMini extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showDrivers: false,
      miniHover: false,
      showAllModels: props.isDeployList === true ? true : false,
      isPartial: false,
      modelSearch: undefined,
      mode: "Explore",
      capacitySelection: 50,
      sliderView: "Feedback",
      isPersonalize: false,
      searchColumn: undefined
    };

    if (window.tableau) {
      const markSelection = window.tableau.TableauEventType.MarkSelectionChanged;

      window.tableau.extensions.initializeDialogAsync().then(() => {
        const dashboard = window.tableau.extensions.dashboardContent.dashboard;

        for (const worksheet of dashboard.worksheets) {
          const unregisterEventHandlerFunction = worksheet.addEventListener(markSelection, (selectionEvent) => {
            worksheet.getSelectedMarksAsync().then((marks) => {
              const columnName = marks?.data?.[0]?._columns?.[0]?._fieldName;
              const valueName = marks?.data?.[0]?._data?.[0]?.[0]?._formattedValue;

              this.setState({ searchColumn: columnName });

              props.updateSearchType("All Insights");

              props.handleSearchInsights([
                {
                  filterField: columnName,
                  filterValue: valueName,
                  label: columnName + ": " + valueName,
                  value: columnName + ": " + valueName
                }
              ]);
            });
          });
        }
      });
    }
  }

  getFormattedDrivers(prediction) {
    const { colSelections } = this.props;

    if (prediction === undefined) {
      return undefined;
    }

    var drivers = prediction;

    drivers.sort(function (a, b) {
      if (a.impact > b.impact) return 1;
      if (b.impact > a.impact) return -1;

      return 0;
    });

    var formattedDrivers = [];

    var isLargestPositive = true;
    var isLargestNegative = true;

    for (var i = 0; i < drivers.length; i++) {
      var driver = drivers[i];
      var value = colSelections[driver.variable];

      if (formattedDrivers.length >= 3) {
        break;
      } else if (value !== undefined && driver.impact != 0) {
        var label = driver.variable + ": ";
        label += value + " had ";
        var direction;

        if (driver.impact < 0) {
          if (isLargestNegative) {
            label += "the largest ";
            isLargestNegative = false;
          } else {
            label += "a ";
          }

          label += "negative impact";
          direction = "Negative";
        } else {
          if (isLargestPositive) {
            label += "the largest ";
            isLargestPositive = false;
          } else {
            label += "a ";
          }

          label += "positive impact";
          direction = "Positive";
        }

        formattedDrivers.push({
          label: label,
          direction: direction
        });
      }
    }

    return formattedDrivers;
  }

  renderPredictionNarrative(formattedDrivers) {
    const { showDrivers } = this.state;

    return (
      <div className={styles.dHoldNarrative} style={showDrivers ? { display: "none" } : { paddingLeft: "20px" }}>
        <div className={styles.dHeader}>{"Top Prediction Drivers"}</div>
        {formattedDrivers ? (
          formattedDrivers.map((f, i) => (
            <div className={styles.dFieldPredict} key={i}>
              {f.direction === "Positive" ? (
                <div className={styles.dItem}>
                  <span className={styles.dIconGood}>
                    <FontAwesomeIcon icon={faArrowUp} />
                  </span>
                </div>
              ) : (
                <div className={styles.dItem}>
                  <span className={styles.dIconBad}>
                    <FontAwesomeIcon icon={faArrowDown} />
                  </span>
                </div>
              )}
              <div className={styles.dItem}>{f.label}</div>
            </div>
          ))
        ) : (
          <div>{"No Row level drivers found"}</div>
        )}
      </div>
    );
  }

  renderPersonalization() {
    const { id, model, bestModel, models, defaultCapacityModels, orgGlobalModel, processLimit, defaultProcessLimit } =
      this.props;

    if (orgGlobalModel === undefined) {
      return <div />;
    }

    const currentGlobalModel = models && models.filter((m) => m.id === orgGlobalModel.id)[0];
    const currentModelDefaultCapacity =
      defaultCapacityModels && defaultCapacityModels.filter((m) => m.id === model.id)[0];

    let diffFeatures = {};

    if (
      bestModel &&
      orgGlobalModel &&
      bestModel.id !== orgGlobalModel.id &&
      bestModel.features.percentages &&
      orgGlobalModel.features.percentages
    ) {
      Object.keys(bestModel.features.percentages).map((p) =>
        Object.keys(bestModel.features.percentages[p].values).forEach((v) => {
          const m1Feature = bestModel.features.percentages[p].values[v];
          const m2Feature = orgGlobalModel.features.percentages[p].values[v];

          const diff = (m2Feature - m1Feature) * 100;

          diffFeatures[p + ": " + v] = {
            val: diff,
            m1: m1Feature,
            m2: m2Feature
          };
        })
      );
    }

    const sortedDiffFeatures = Object.keys(diffFeatures)
      .map((d) => ({ pct: diffFeatures[d].val, label: d }))
      .sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));

    return (
      <div>
        <ChooseProcessLimitFrontierContainer id={id} globalModel={orgGlobalModel} />
        {bestModel && orgGlobalModel && bestModel.id === orgGlobalModel.id ? (
          this.renderModelDrivers()
        ) : model && orgGlobalModel && currentGlobalModel && currentModelDefaultCapacity ? (
          <div className={styles.personalize}>
            {bestModel && orgGlobalModel && (
              <div className={styles.bold}>
                {"You selected " +
                  (bestModel.id === orgGlobalModel.id
                    ? "the org average"
                    : processLimit > defaultProcessLimit
                      ? "a more aggressive"
                      : "a more conservative") +
                  " model: "}
                <span className={styles.slideSpan}>{bestModel.label}</span>
              </div>
            )}
            <div className={styles.processBox}>
              <div className={styles.processLeft}>
                <div>
                  <FontAwesomeIcon
                    icon={processLimit > defaultProcessLimit ? faArrowCircleUp : faArrowCircleDown}
                    style={{ marginRight: "5px" }}
                  />
                  {Math.abs(Math.round(((defaultProcessLimit - processLimit) / processLimit) * 100)) + "%"}
                </div>
                <div className={styles.smallText}>
                  {processLimit > defaultProcessLimit ? "MORE LEADS" : "FEWER LEADS"}
                </div>
              </div>
              <div className={styles.processRight}>{"COMPARED TO A TYPICAL SALESPERSON"}</div>
            </div>
            <div className={styles.bold} style={{ marginTop: "10px" }}>
              {"Your Risk-Reward Tradeoff"}
            </div>
            <div className={styles.boxes}>
              <div className={styles.box + " " + styles.goodColorBack}>
                <div>{"Revenue Increase"}</div>
                <div>
                  <FontAwesomeIcon icon={faArrowCircleUp} style={{ marginRight: "5px" }} />
                  <span>
                    {Math.round(
                      (Math.abs(bestModel.checkMax - orgGlobalModel.checkMax) * 100) /
                        ((bestModel.checkMax + orgGlobalModel.checkMax) / 2)
                    ) + "%"}
                  </span>
                </div>
                <div className={styles.smallText}>
                  {"IF YOU PURSUE " +
                    Math.abs(Math.round(((defaultProcessLimit - processLimit) / processLimit) * 100)) +
                    "% " +
                    (processLimit > defaultProcessLimit ? "MORE LEADS" : "FEWER LEADS")}
                </div>
              </div>
              <div className={styles.box + " " + styles.badColorBack}>
                <div>{"Revenue Loss"}</div>
                <div>
                  <FontAwesomeIcon icon={faArrowCircleDown} style={{ marginRight: "5px" }} />
                  <span>
                    {Math.round(
                      (Math.abs(currentGlobalModel.checkMax - currentModelDefaultCapacity.checkMax) * 100) /
                        ((Math.abs(currentGlobalModel.checkMax) + Math.abs(currentModelDefaultCapacity.checkMax)) / 2)
                    ) + "%"}
                  </span>
                </div>

                <div className={styles.smallText}>{"IF YOU HAD PURSUED ALL LEADS"}</div>
              </div>
            </div>
            <div className={styles.diffDrivers}>
              <div className={styles.bold}>
                {"The things most different about your model compared to the organization"}
              </div>
              <div className={styles.diffContent}>
                {sortedDiffFeatures
                  .filter((d, i) => i < 30)
                  .map((d) => (
                    <div className={styles.diffFeature}>
                      <div className={styles.dPct + " " + (d.pct < 0 ? styles.badColor : styles.goodColor)}>
                        {(d.pct > 0 ? "+" : "") + d.pct.toFixed(2) + "%"}
                      </div>
                      <div className={styles.dLabel}>{d.label}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          <div></div>
        )}
      </div>
    );
  }

  renderFeedback() {
    const {
      projectId,
      capModels,
      isOverallSegment,
      isContinuous,
      selectedScenario,
      modelGroups,
      optimizer,
      activeSegment,
      updateCapValue,
      updateModelFeedback
    } = this.props;
    const { capacitySelection, sliderView } = this.state;

    const drilldown =
      modelGroups &&
      optimizer &&
      optimizer.drilldownValue &&
      modelGroups.drilldownValues &&
      modelGroups.drilldownValues.filter((d) => d.value === optimizer.drilldownValue).length > 0
        ? modelGroups.drilldownValues.filter((d) => d.value === optimizer.drilldownValue)[0]
        : undefined;

    const selectedModel = drilldown ? capModels[drilldown.capValue + "%"] : capModels[capacitySelection + "%"];

    let retModels = {};

    Object.keys(capModels).forEach((c) => {
      let predictColor = "#E6E6FC";

      if (selectedScenario) {
        selectedScenario.predictions.forEach((p) => {
          if (p.model === capModels[c].id) {
            predictColor = p.prediction === "0.0" ? "rgba(158, 31, 20, 1)" : "rgba(123, 203, 126, 1)";
          }
        });
      }

      if (sliderView === "Result") {
        retModels[c] = {
          model_data: capModels[c],
          color: predictColor
        };
      } else if (sliderView === "Frontier") {
        retModels[c] = { model_data: capModels[c] };
      } else if (sliderView === "Feedback") {
        retModels[c] = {
          model_data: capModels[c],
          color:
            selectedModel &&
            activeSegment &&
            activeSegment.job &&
            activeSegment.job.data &&
            activeSegment.job.data[capModels[c].id]
              ? activeSegment.job.data[capModels[c].id] > 0
                ? "#5BCF74"
                : "#F8CD7E"
              : "#E6E6FC"
        };
      } else {
        retModels[c] = {
          model_data: capModels[c],
          color: selectedModel && selectedModel.id === capModels[c].id ? "rgba(122, 109, 208, 1)" : "#E6E6FC"
        };
      }
    });

    return (
      <div>
        {isContinuous !== true && (
          <CapSlider
            capModels={retModels}
            onChange={(val) => updateCapValue(projectId, val)}
            currentImpact={drilldown ? drilldown.capValue : 50}
            isWizard
          />
        )}
        <div className={styles.feedActions}>
          <ButtonGroup>
            <Button onClick={() => updateModelFeedback(selectedModel.id, true)}>
              <FontAwesomeIcon icon={faThumbsUp} />
            </Button>
            <Button onClick={() => updateModelFeedback(selectedModel.id, false)}>
              <FontAwesomeIcon icon={faThumbsDown} />
            </Button>
          </ButtonGroup>
        </div>
        {isContinuous !== true && (
          <div className={styles.feedActions}>
            <ButtonGroup>
              <Button onClick={() => this.setState({ sliderView: "Frontier" })} active={sliderView === "Frontier"}>
                {"Frontier"}
              </Button>
              <Button onClick={() => this.setState({ sliderView: "Selected" })} active={sliderView === "Selected"}>
                {"Selected"}
              </Button>
              <Button onClick={() => this.setState({ sliderView: "Feedback" })} active={sliderView === "Feedback"}>
                {"Feedback"}
              </Button>
              <Button onClick={() => this.setState({ sliderView: "Result" })} active={sliderView === "Result"}>
                {"Result"}
              </Button>
            </ButtonGroup>
          </div>
        )}
      </div>
    );
  }

  renderFooter() {
    const {
      projectId,
      capModels,
      isOverallSegment,
      colSelections,
      isContinuous,
      selectedScenario,
      modelGroups,
      optimizer,
      capValue,
      username,
      activeSegment,
      predictAll,
      updateCapValue,
      updateModelFeedback
    } = this.props;
    const { capacitySelection, sliderView, isPersonalize } = this.state;

    const drilldown =
      modelGroups &&
      optimizer &&
      optimizer.drilldownValue &&
      modelGroups.drilldownValues &&
      modelGroups.drilldownValues.filter((d) => d.value === optimizer.drilldownValue).length > 0
        ? modelGroups.drilldownValues.filter((d) => d.value === optimizer.drilldownValue)[0]
        : undefined;

    const selectedModel = drilldown
      ? capModels[Math.round(drilldown.capValue) + "%"]
      : capModels[capacitySelection + "%"];

    const mFeedback =
      selectedModel &&
      activeSegment &&
      activeSegment.roles &&
      activeSegment.roles.length > 0 &&
      activeSegment.roles[0].preferences &&
      activeSegment.roles[0].preferences.feedback &&
      activeSegment.roles[0].preferences.feedback[selectedModel.id]
        ? activeSegment.roles[0].preferences.feedback[selectedModel.id]
        : undefined;

    let retModels = {};

    Object.keys(capModels).forEach((c) => {
      let predictColor = "#E6E6FC";

      if (selectedScenario) {
        selectedScenario.predictions.forEach((p) => {
          if (p.model === capModels[c].id) {
            predictColor =
              capModels[c] &&
              capModels[c].maxIteration &&
              capModels[c].maxIteration.threshold !== undefined &&
              p.probability < capModels[c].maxIteration.threshold * 100
                ? "rgba(158, 31, 20, 1)"
                : "rgba(123, 203, 126, 1)";
          }
        });
      }

      if (sliderView === "Result") {
        retModels[c] = {
          model_data: capModels[c],
          color: predictColor
        };
      } else if (sliderView === "Frontier") {
        retModels[c] = { model_data: capModels[c] };
      } else if (sliderView === "Feedback") {
        const retFeedback =
          activeSegment &&
          activeSegment.roles &&
          activeSegment.roles.length > 0 &&
          activeSegment.roles[0].preferences &&
          activeSegment.roles[0].preferences.feedback &&
          activeSegment.roles[0].preferences.feedback[capModels[c].id]
            ? activeSegment.roles[0].preferences.feedback[capModels[c].id]
            : undefined;

        retModels[c] = {
          model_data: capModels[c],
          color: retFeedback && retFeedback > 0 ? "#5BCF74" : retFeedback && retFeedback < 0 ? "#F8CD7E" : "#E6E6FC"
        };
      } else {
        retModels[c] = {
          model_data: capModels[c],
          color: selectedModel && selectedModel.id === capModels[c].id ? "rgba(122, 109, 208, 1)" : "#E6E6FC"
        };
      }
    });

    const selOptions = colSelections
      ? Object.keys(colSelections)
          .filter((c) => colSelections[c] !== "Blank-Aible")
          .map((c) => ({ field: c, value: colSelections[c] }))
      : undefined;

    return (
      <div className={styles.wFooter}>
        <div
          className={styles.frontierFooter}
          style={
            isPersonalize
              ? {
                  maxHeight: "200px",
                  padding: "20px",
                  paddingTop: "0px",
                  marginBottom: "20px",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.2)"
                }
              : { maxHeight: "0px", padding: "0px" }
          }
        >
          <div className={styles.fMsg}>{"Personalize Your Predictions"}</div>
          <div className={styles.subMsg}>
            <div>{"CONSERVATIVE"}</div>
            <div>{"AGGRESSIVE"}</div>
          </div>
          <CapSlider
            capModels={retModels}
            onChange={(val) => updateCapValue(projectId, val)}
            currentImpact={drilldown ? drilldown.capValue : 50}
            isWizard
          />
          <div className={styles.feedActions}>
            <ButtonGroup>
              <Button onClick={() => this.setState({ sliderView: "Frontier" })} active={sliderView === "Frontier"}>
                {"Frontier"}
              </Button>
              <Button onClick={() => this.setState({ sliderView: "Selected" })} active={sliderView === "Selected"}>
                {"Selected"}
              </Button>
              <Button onClick={() => this.setState({ sliderView: "Feedback" })} active={sliderView === "Feedback"}>
                {"Feedback"}
              </Button>
              <Button onClick={() => this.setState({ sliderView: "Result" })} active={sliderView === "Result"}>
                {"Result"}
              </Button>
            </ButtonGroup>

            <div className={styles.pAll}>
              {capValue !== undefined && (
                <Button onClick={() => updateCapValue(projectId, undefined)}>{"Reset to Optimal"}</Button>
              )}
              {sliderView === "Result" && isEnabled("predict-all") && (
                <Button onClick={() => predictAll()} disabled={selOptions && selOptions.length === 0}>
                  {"Predict All"}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.wAction}>
          <Button
            onClick={() => updateModelFeedback(selectedModel.id, false)}
            active={mFeedback !== undefined && mFeedback < 0}
          >
            <div className={styles.wButton}>
              <img src={ThumbsDown} className={styles.wIcon} />
              {"I don't like this model"}
            </div>
          </Button>
          <Button
            onClick={() => updateModelFeedback(selectedModel.id, true)}
            active={mFeedback !== undefined && mFeedback > 0}
          >
            <div className={styles.wButton}>
              <img src={ThumbsUp} className={styles.wIcon} />
              {"I like this model"}
            </div>
          </Button>
          <Button onClick={() => this.setState({ isPersonalize: !isPersonalize })} active={isPersonalize}>
            <div className={styles.wButton}>
              <img src={SettingsIcon} className={styles.wIcon} />
              {"Personalize"}
            </div>
          </Button>
        </div>
      </div>
    );
  }

  renderAssumptions() {
    const {
      id,
      setup,
      initCustomBlueprint,
      updateConversionGain,
      updateConversionCost,
      updateCustomValue,
      blueprint,
      blueprints
    } = this.props;

    //    updateCustomValue: (runId, blueprintId, valId, value) => {

    const { tBlueprint } = this.state;

    return setup === undefined ? (
      <div />
    ) : (
      <div className={styles.blueprintHolder}>
        <div style={{ marginBottom: "20px", marginTop: "-20px" }}>
          <div>{"Select a blueprint"}</div>
          <div style={{ zIndex: 5 }}>
            <Select
              options={
                blueprints
                  ? [
                      { label: "Continue Without Blueprint", value: "NONE" },
                      ...blueprints.map((b) => ({
                        label: b.name,
                        value: b.id,
                        blueprint: b
                      }))
                    ]
                  : []
              }
              value={
                tBlueprint && blueprints && blueprints.filter((b) => b.id === tBlueprint.id).length > 0
                  ? {
                      label: blueprints.filter((b) => b.id === tBlueprint.id)[0].name,
                      value: blueprints.filter((b) => b.id === tBlueprint.id)[0].id
                    }
                  : { label: "Continue Without Blueprint", value: "NONE" }
              }
              menuPlacement="bottom"
              onChange={(val) => {
                this.setState({ tBlueprint: val.blueprint });
                initCustomBlueprint(val.value);
              }}
            />
          </div>
        </div>

        {tBlueprint !== undefined && (
          <div className={styles.aHolder}>
            {Object.keys(setup.customValues).map((v, i) => (
              <div style={{ marginTop: "20px" }}>
                <LineInput
                  isMediumBox
                  topLabel={v}
                  labelStyle={{ zIndex: 0 }}
                  value={setup.customValues[v]}
                  onChange={(val) => updateCustomValue(id, tBlueprint.id, v, val)}
                />
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: "20px" }}>
          <LineInput
            isMediumBox
            labelStyle={{ zIndex: 0 }}
            topLabel={setup.truePositiveLabel}
            defaultValue={setup.conversionGain}
            onChange={(val) => updateConversionGain(val)}
          />
        </div>
        <div style={{ marginTop: "20px" }}>
          <LineInput
            isMediumBox
            labelStyle={{ zIndex: 0 }}
            topLabel={setup.falsePositiveLabel}
            defaultValue={setup.conversionCost}
            onChange={(val) => updateConversionCost(val)}
          />
        </div>
      </div>
    );
  }

  renderSummary() {
    const { isContinuous, model, scenarios, isOverallSegment, scenarioIndex } = this.props;
    const { showDrivers, largeScenario, mode } = this.state;

    const scenario =
      scenarios && scenarios.filter((s) => s.id === scenarioIndex).length > 0
        ? scenarios.filter((s) => s.id === scenarioIndex)[0]
        : undefined;

    const prediction = scenario ? scenario.predictions : undefined;

    const retPrediction =
      prediction && prediction[0] && prediction[0].info && prediction[0].info.rowLevelDrivers
        ? prediction[0].info.rowLevelDrivers
        : undefined;

    const formattedDrivers = this.getFormattedDrivers(retPrediction);

    return (
      <div
        className={styles.drivers}
        style={
          showDrivers
            ? {
                marginTop: largeScenario ? "40vh" : "80vh",
                paddingTop: "0px",
                height: largeScenario ? "calc(100% - 40vh)" : "100%"
              }
            : {}
        }
      >
        {this.renderPredict()}
        <div
          className={styles.dHold}
          style={
            mode === "Explore"
              ? { height: "calc(100% - 90px)" }
              : showDrivers
                ? { display: "none" }
                : isContinuous
                  ? { paddingBottom: "0px" }
                  : {}
          }
        >
          <div className={styles.tabHolder} style={{ display: "none" }}>
            {/*
             // TODO: Bring back personalize
            <div
              className={
                styles.tab +
                " " +
                (mode === "Personalization" ? styles.activeTab : "")
              }
              onClick={() => this.setState({ mode: "Personalization" })}
            >
              {"Personalize"}
            </div>
          */}
            <div
              className={styles.tab + " " + (mode === "Feedback" ? styles.activeTab : "")}
              onClick={() => this.setState({ mode: "Feedback" })}
            >
              {"Feedback"}
            </div>
            <div
              className={styles.tab + " " + (mode === "ModelDrivers" ? styles.activeTab : "")}
              onClick={() => this.setState({ mode: "ModelDrivers" })}
            >
              {"Model Drivers"}
            </div>
            <div
              className={styles.tab + " " + (mode === "PredictDrivers" ? styles.activeTab : "")}
              onClick={() => this.setState({ mode: "PredictDrivers" })}
            >
              {"Prediction Drivers"}
            </div>
          </div>
          {mode === "Explore"
            ? this.renderExplore()
            : mode === "Feedback"
              ? this.renderFeedback()
              : mode === "Personalization"
                ? this.renderPersonalization()
                : mode === "PredictDrivers"
                  ? this.renderPredictionNarrative(formattedDrivers)
                  : mode === "Assumptions"
                    ? this.renderAssumptions()
                    : this.renderDrivers()}
          {isContinuous !== true && mode !== "Explore" && this.renderFooter()}
        </div>
      </div>
    );
  }

  renderModelDrivers() {
    const { model, getModelFeature } = this.props;

    const featureBars =
      model && model.featureInfo && model.featureInfo.length > 0
        ? model.featureInfo.map((f) => ({
            left: f.score,
            label: f.label,
            leftModel: model.id,
            values: getModelFeature(model.id, f.label).values
          }))
        : [];

    return (
      <div className={styles.modelDrivers}>
        <div className={styles.dHeader}>{"Top Model Drivers"}</div>
        {featureBars &&
          featureBars
            .filter((f, i) => i < 3)
            .map((f, i) => {
              let maxValue = undefined;
              let maxKey = undefined;

              let minValue = undefined;
              let minKey = undefined;

              for (const val of Object.keys(f.values)) {
                if (maxValue === undefined || f.values[val] >= maxValue) {
                  maxValue = f.values[val];
                  maxKey = val;
                }

                if (minValue === undefined || f.values[val] <= minValue) {
                  minValue = f.values[val];
                  minKey = val;
                }
              }

              return (
                <div className={styles.dField} key={i}>
                  <div className={styles.dItem}>
                    <span className={styles.bold}>{f.label}</span>
                    <span>
                      {i === 0
                        ? " is the most impactful driver"
                        : i === 1
                          ? " is the second most impactful driver"
                          : i === 2
                            ? " is the third most impactful driver"
                            : " is an impactful driver"}
                    </span>
                  </div>
                  <div className={styles.dItem}>
                    <span className={styles.dIconGood}>
                      <FontAwesomeIcon icon={faArrowUp} />
                    </span>
                    <span className={styles.bold}>{maxKey}</span>
                    <span>{" is the strongest positive"}</span>
                  </div>
                  <div className={styles.dItem}>
                    <span className={styles.dIconBad}>
                      <FontAwesomeIcon icon={faArrowDown} />
                    </span>
                    <span className={styles.bold}>{minKey}</span>
                    <span>{" is the strongest negative"}</span>
                  </div>
                </div>
              );
            })}
      </div>
    );
  }

  renderDrivers() {
    const { id, projectId } = this.props;

    return (
      <div className={styles.driverHolder}>
        <FeatureChartContainer
          id={id}
          isHideSelect
          predictMode={"predict"}
          featureStyle={{
            border: "none",
            margin: "0px",
            background: "#EEEFF4"
          }}
          isTabMini
          projectId={projectId}
          hideRight
        />
      </div>
    );
  }

  renderPredict() {
    const { id, projectId, segmentOptions, updateDrilldownValue, outcome, setup } = this.props;
    const { showDrivers, largeScenario, mode } = this.state;

    const sortedSegmentOptions = [...segmentOptions]
      .filter((s) => s.label !== "Overall")
      .sort((a, b) => (a.label < b.label ? -1 : 1));

    return (
      <div
        className={styles.predictHolder}
        onClick={showDrivers ? () => this.setState({ largeScenario: true }) : () => {}}
      >
        <div className={styles.topHeader}>
          <img src={BullseyeLight} style={{ height: "20px", width: "20px", marginRight: "5px" }} />
          {outcome === undefined
            ? "..."
            : setup && setup.success && setup.success.value
              ? "Predict: " + outcome + " is " + setup.success.value
              : "Predict: " + outcome}
          {sortedSegmentOptions && sortedSegmentOptions.length > 1 ? (
            <div className={styles.subgroupSettings} onClick={() => updateDrilldownValue(false, "Overall")}>
              <Button style={{ padding: "4px" }}>
                <FontAwesomeIcon icon={faCog} />
              </Button>
            </div>
          ) : (
            ""
          )}
        </div>
        <div className={styles.menuHolder}>
          <div
            className={styles.menuTab + " " + (mode === "Explore" ? styles.menuTabSelected : "")}
            onClick={() => this.setState({ mode: "Explore" })}
          >
            {"Explore"}
          </div>
          <div
            className={styles.menuTab + " " + (mode === "ModelDrivers" ? styles.menuTabSelected : "")}
            onClick={() => this.setState({ mode: "ModelDrivers" })}
          >
            {"What-If Scenario"}
          </div>
          <div
            className={styles.menuTab + " " + (mode === "PredictDrivers" ? styles.menuTabSelected : "")}
            onClick={() => this.setState({ mode: "PredictDrivers" })}
          >
            {"Prediction Drivers"}
          </div>
        </div>
        {mode === "Explore" ? (
          <div />
        ) : (
          <FeatureChartContainer
            id={id}
            isSmall
            predictMode={"predict"}
            featureStyle={{ display: "none" }}
            isConsumer
            projectId={projectId}
          />
        )}
      </div>
    );
  }

  renderGoodChart() {
    const { searchColumn } = this.state;

    return (
      <div className={styles.goodBox} style={{ background: "rgba(220, 245, 235, 1)" }}>
        <div className={styles.gIcon}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#34D399" }} />
        </div>
        <div>
          <div className={styles.wBold}>{"This is a good chart to keep"}</div>
          {"A strong relationship exists between " + searchColumn + " and all other variables"}
        </div>
      </div>
    );
  }

  renderBadChart() {
    return (
      <div className={styles.goodBox} style={{ background: "rgba(248, 230, 194, 1)" }}>
        <div className={styles.gIcon}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        <div>
          <div className={styles.wBold}>{"This may be a misleading chart"}</div>
          {"Charts where a variable with low quality is in the horizontal axis " +
            "may be statistically less informative and might mislead you."}
        </div>
      </div>
    );
  }

  renderExplore() {
    const { dataset, handleSearchInsights, nextSearchInsights, nextId } = this.props;
    const { searchColumn } = this.state;

    const searchScores = dataset?.interaction_scores_lookup?.[searchColumn] ?? {};

    const goodScore = Object.keys(searchScores)
      .filter((fKey) => fKey != searchScores)
      .map((fKey) => searchScores[fKey])
      .reduce((x, y) => x + y, 0);

    const isGood = goodScore > 0.1;

    return (
      <div className={styles.explore}>
        <NextSearchContainer id={nextId} datasetId={dataset?.id} isOnlySearch />
        <div style={{ height: "60px" }} />
        {searchColumn === undefined ? <div /> : isGood ? this.renderGoodChart() : this.renderBadChart()}
        <div className={styles.mHolder}>
          <div className={styles.muscleChart} style={nextSearchInsights ? { pointerEvents: "none" } : {}}>
            <LeakageChartContainer id={nextId} isTableau />
          </div>
        </div>
        {nextSearchInsights && (
          <div style={{ marginTop: "50px" }}>
            {nextSearchInsights
              .sort((a, b) => (a?.metadata?.type === "ModelDriver" ? -1 : b?.metadata?.type === "ModelDriver" ? 1 : 0))
              .map((node) => (
                <div className={styles.mHolder}>
                  <div className={styles.nextChart}>
                    <NextChartContainer
                      id={nextId}
                      // TODO: Madness please fix this. To show muscle char
                      node={{
                        chart: { chart: node, fields: node.fields },
                        fields: node.fields
                      }}
                      chart={{ ...node, chart: node.chart }}
                      isReport={true}
                      showTooltip={true}
                      showLeakage={true}
                      isTableauMode={true}
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  }

  renderMiniHeader() {
    const { aibleMode, outcome, datasetLabel, model, created } = this.props;
    const { miniHover } = this.state;

    return (
      <div
        className={styles.miniHeader}
        onMouseEnter={() => this.setState({ miniHover: true })}
        onMouseLeave={() => this.setState({ miniHover: false })}
      >
        {miniHover ? (
          <div className={styles.modelInfo}>
            <div className={styles.imgContainerLarge}>
              <img src={aibleMode == "academic" ? AcademicBlue : BullseyeBlue} />
            </div>
            <div>
              <div className={styles.boldLine}>{"Predict " + outcome}</div>
              <div className={styles.line}>{"Data: " + datasetLabel}</div>
              <div className={styles.line}>{"Model Type: " + model.label}</div>
              <div className={styles.line}>{"Created: " + created}</div>
            </div>
          </div>
        ) : (
          <div className={styles.line}>
            <div className={styles.imgContainer}>
              <img src={aibleMode == "academic" ? AcademicBlue : BullseyeBlue} />
            </div>
            {"Predict " + outcome}
          </div>
        )}
      </div>
    );
  }

  renderAllModels() {
    const { deployments, openDeployment, history } = this.props;
    const { modelSearch } = this.state;

    return (
      <div className={styles.aWizard}>
        <div className={styles.allHeader}>
          {deployments && deployments.length > 0 ? "Select a model to use" : "Loading Models..."}
          {deployments.length > 0 && (
            <LineInput
              isMediumBox
              placeholder={"Search..."}
              style={{
                width: "calc(100% - 170px)",
                justifyContent: "flex-end"
              }}
              onChange={(val) => this.setState({ modelSearch: val })}
              updateInstant
            />
          )}
        </div>
        <div className={styles.wizardAll}>
          {deployments && deployments.length > 0 ? (
            deployments.map((d, i) => (
              <div key={i}>
                <WizardModelContainer
                  id={d.job_id}
                  isFull
                  onClick={() => openDeployment(d)}
                  modelId={d.model_name}
                  search={modelSearch}
                />
              </div>
            ))
          ) : (
            <div className={styles.modelLoading}>
              <LoadingIndicator style={{ margin: "0px", padding: "0px", marginLeft: "-20px" }} />
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      id,
      blueprintId,
      model,
      models,
      outcome,
      fieldSelect,
      isTableauMode,
      isOverallSegment,
      expectedModelCount,
      orgGlobalModel,
      segmentOptions,
      optimizer,
      overallSegmentId,
      updateDrilldownValue,
      updateFieldSelect,
      status,
      history,
      isContinuous
    } = this.props;
    const { showDrivers, isPartial, showAllModels, mode } = this.state;

    if (status && status === "ERROR") {
      return (
        <div className={styles.loading}>
          <div>{"Error loading models"}</div>
          <Button onClick={() => history.push("/")} style={{ marginTop: "10px" }}>
            {"Back"}
          </Button>
        </div>
      );
    }

    if (models === undefined || models.length < 1) {
      return (
        <div className={styles.loading}>
          <div>{"Training AI Models...."}</div>
          {/*
          <div>{"Training " + models.length +  " of " + expectedModelCount + " Models...."}</div>
          */}
          <LoadingIndicator style={{ margin: "0px", marginTop: "20px" }} />
        </div>
      );
    }

    if (showAllModels) {
      return this.renderAllModels();
    }

    const selectedFeatureBars =
      fieldSelect &&
      model &&
      model.features &&
      model.features.percentages &&
      model.features.percentages[fieldSelect] &&
      model.features.percentages[fieldSelect].values
        ? Object.keys(model.features.percentages[fieldSelect].values).map((value) => ({
            value,
            percentage: model.features.percentages[fieldSelect].values[value]
          }))
        : [];

    const labelData = {
      label: model && model.label,
      score:
        fieldSelect && model.features.percentages[fieldSelect]
          ? model.features.percentages[fieldSelect].percentage
          : "",
      baseline: model && model.features && model.features.comparison_mean
    };

    const hasPartialDep =
      model && model.plots && fieldSelect && model.plots.filter((p) => p.variable_name === fieldSelect).length > 0;

    const sortedSegmentOptions = [...segmentOptions]
      .filter((s) => s.label !== "Overall")
      .sort((a, b) => (a.label < b.label ? -1 : 1));

    return (
      <div className={styles.wizardMini}>
        {isOverallSegment ? (
          <div style={{ marginTop: "20px" }}>
            <div className={styles.bHeader}>{"Please select a subgroup to begin using Aible for One"}</div>
            <div className={styles.segmentSwitcher}>
              <Select
                options={sortedSegmentOptions}
                value={
                  optimizer.drilldownValue === "Overall"
                    ? undefined
                    : {
                        value: optimizer.drilldownValue,
                        label: optimizer.drilldownValue
                      }
                }
                placeholder={"Select a Subgroup"}
                onChange={(v) => updateDrilldownValue(v.runId, v.value)}
                className={styles.rSelect}
              />
            </div>
          </div>
        ) : (
          <div>{this.renderSummary()}</div>
        )}
        <div className={styles.partialHolder} style={fieldSelect ? undefined : { bottom: "-550px" }}>
          <div className={styles.bHeader}>{fieldSelect}</div>
          <div className={styles.closeHolder}>
            <ButtonGroup>
              <Button type="secondary" onClick={() => this.setState({ isPartial: false })} active={!isPartial}>
                {"Driver Details"}
              </Button>
              <Button
                type="secondary"
                onClick={() => this.setState({ isPartial: true })}
                active={isPartial}
                disabled={hasPartialDep === false}
                title={
                  hasPartialDep
                    ? "Marginal Effects"
                    : "Not available for this model (" + (model ? model.label : "") + ")"
                }
              >
                {"Marginal Effects"}
              </Button>
            </ButtonGroup>
            <Button
              type="secondary"
              onClick={() => updateFieldSelect(undefined)}
              style={{ position: "absolute", right: "20px", top: "20px" }}
            >
              {"Close"}
            </Button>
          </div>
          <div style={{ height: "390px" }}>
            {isPartial ? (
              <PartialDepPlotContainer
                model={model}
                fieldSelect={fieldSelect}
                updateFieldSelect={updateFieldSelect}
                isFieldSelect={fieldSelect !== undefined}
              />
            ) : (
              <div className={styles.predictionDriversCompanionContent}>
                <SelectedFeatureChart
                  bars={selectedFeatureBars}
                  fieldSelect={fieldSelect}
                  isContinuous={isContinuous}
                  labelData={labelData}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
